import { colors, createStyles, makeStyles, Theme } from '@material-ui/core';

const NAME_COLUMN = 120;
const VERSION_COLUMN = 160;
const ACTIONS_COLUMN = 200;
const COLUMN_GAP = 24;
const ROW_PADDING = 16;

const columns = `${NAME_COLUMN}px ${VERSION_COLUMN}px minmax(0, 1fr) ${ACTIONS_COLUMN}px`;

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    description: {
      maxWidth: 1400,
      marginTop: theme.spacing(2),
    },
    sheet: {
      maxWidth: 1400,
    },
    header: {
      display: 'none',
      [theme.breakpoints.up('md')]: {
        display: 'grid',
        gridTemplateColumns: columns,
        columnGap: COLUMN_GAP,
        alignItems: 'center',
        minHeight: 40,
        padding: `0 ${ROW_PADDING}px`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontSize: 13,
        color: colors.grey[700],
      },
    },
    headerRule: {
      display: 'flex',
      alignItems: 'center',
    },
    platform: {
      borderTop: `1px solid ${theme.palette.divider}`,
      '&:first-of-type': {
        borderTop: 'none',
      },
    },
    row: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      gridTemplateAreas: `
        "name add"
        "version version"
        "rule rule"
        "previous previous"
      `,
      gap: theme.spacing(1, 2),
      alignItems: 'center',
      padding: ROW_PADDING,
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: columns,
        gridTemplateAreas: `
          "name version rule add"
          ". . previous previous"
        `,
        columnGap: COLUMN_GAP,
        alignItems: 'start',
      },
    },
    nameCell: {
      gridArea: 'name',
      [theme.breakpoints.up('md')]: {
        lineHeight: '36px',
      },
    },
    versionCell: {
      gridArea: 'version',
    },
    versionValue: {
      fontVariantNumeric: 'tabular-nums',
      [theme.breakpoints.up('md')]: {
        lineHeight: '36px',
      },
    },
    ruleCell: {
      gridArea: 'rule',
      minWidth: 0,
    },
    addCell: {
      gridArea: 'add',
      justifySelf: 'end',
    },
    previousCell: {
      gridArea: 'previous',
      fontSize: 13,
      color: colors.grey[700],
      fontVariantNumeric: 'tabular-nums',
    },
    // Кнопка выглядит как ссылка внутри строки, поэтому наследует шрифт и базовую линию текста.
    historyLink: {
      font: 'inherit',
      verticalAlign: 'baseline',
      color: colors.blue[600],
    },
    // На широком экране подписи заменяет шапка колонок, но для скринридера они остаются.
    cellLabel: {
      display: 'block',
      fontSize: 13,
      color: colors.grey[700],
      [theme.breakpoints.up('md')]: {
        position: 'absolute',
        width: 1,
        height: 1,
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
      },
    },
    zones: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      margin: 0,
      padding: 0,
      listStyle: 'none',
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
      },
    },
    zone: {
      flex: '1 1 0',
      minWidth: 0,
      padding: theme.spacing(0.75, 1.5),
      color: theme.palette.text.primary,
      fontSize: 13,
      lineHeight: 1.3,
      '&:first-child': {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderBottomLeftRadius: theme.shape.borderRadius,
      },
      '&:last-child': {
        borderTopRightRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
      },
    },
    zoneMandatory: {
      backgroundColor: colors.amber[100],
    },
    zoneOptional: {
      backgroundColor: colors.grey[100],
    },
    zoneNone: {
      backgroundColor: colors.lightBlue[50],
    },
    zoneRange: {
      display: 'block',
      fontFamily: theme.typography.h4.fontFamily,
      fontVariantNumeric: 'tabular-nums',
    },
    emptyRule: {
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        alignItems: 'center',
        minHeight: 36,
      },
    },
    zoneRequirement: {
      display: 'block',
      color: colors.grey[800],
    },
    ruleNote: {
      padding: ROW_PADDING,
      borderTop: `1px solid ${theme.palette.divider}`,
      fontSize: 13,
      color: colors.grey[700],
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
    },
    checkerForm: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      gap: theme.spacing(1, 2),
    },
    checkerPlatform: {
      flex: '1 1 160px',
      minWidth: 0,
    },
    checkerField: {
      flex: '1 1 240px',
      minWidth: 0,
    },
    checkerResult: {
      flex: '1 1 100%',
      minWidth: 0,
      marginTop: theme.spacing(0.5),
    },
    historyCount: {
      marginTop: theme.spacing(0.5),
      fontSize: 13,
      color: colors.grey[700],
    },
    historyContent: {
      padding: 0,
    },
    historyList: {
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    historyItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      minHeight: 48,
      padding: theme.spacing(1, 3),
      '& + &': {
        borderTop: `1px solid ${theme.palette.divider}`,
      },
    },
    historyVersion: {
      fontSize: 16,
      color: theme.palette.text.primary,
      fontVariantNumeric: 'tabular-nums',
    },
    // Цвет повторяет зону «обязательно» на шкале правила обновления.
    historyMark: {
      padding: theme.spacing(0.25, 1),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: colors.amber[100],
      color: theme.palette.text.primary,
      fontSize: 12,
      lineHeight: 1.4,
      whiteSpace: 'nowrap',
    },
    historyState: {
      padding: theme.spacing(2, 3),
    },
    historyLoading: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    historyMore: {
      padding: theme.spacing(1, 2),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  })
);
